import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, ExternalLink, RefreshCw } from 'lucide-react'
import { fetchOnboardingPfById, finalizeOnboardingPf } from '../../services/api'
import Loader from '../../components/Loader'
import {
	Alert,
	Badge,
	Button,
	Card,
	CopyableId,
	DetailGrid,
	DetailItem,
	EmptyState,
	JsonViewer,
	PageHeader,
	formatCurrency,
	formatDateTime,
	formatDocument,
	statusTone,
} from '../../components/ui'

const OnboardingPfDetails = () => {
	const { id } = useParams()
	const [onboarding, setOnboarding] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState('')
	const [feedback, setFeedback] = useState('')
	const [showRaw, setShowRaw] = useState(false)
	const [isFinalizing, setIsFinalizing] = useState(false)

	const load = useCallback(async () => {
		setIsLoading(true)
		setError('')
		try {
			setOnboarding(await fetchOnboardingPfById(id))
		} catch (err) {
			setError(err.message)
			setOnboarding(null)
		} finally {
			setIsLoading(false)
		}
	}, [id])

	useEffect(() => {
		load()
	}, [load])

	const handleFinalize = async () => {
		const confirmado = window.confirm(
			'Finalizar o onboarding? Dependendo do status atual isso envia os dados para o BaaS.'
		)
		if (!confirmado) return

		setIsFinalizing(true)
		setError('')
		setFeedback('')
		try {
			await finalizeOnboardingPf(id)
			setFeedback('Onboarding finalizado')
			await load()
		} catch (err) {
			setError(err.message)
		} finally {
			setIsFinalizing(false)
		}
	}

	if (isLoading) {
		return (
			<div className="flex h-64 items-center justify-center">
				<Loader size="lg" />
			</div>
		)
	}

	const address = onboarding?.address
	const documents = onboarding?.documents || []
	const phone =
		onboarding?.ddi && onboarding?.ddd && onboarding?.phone
			? `+${onboarding.ddi} (${onboarding.ddd}) ${onboarding.phone}`
			: onboarding?.phone

	return (
		<div>
			<Link
				to="/onboardings"
				className="mb-4 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
			>
				<ArrowLeft className="mr-1 h-4 w-4" />
				Voltar para onboardings
			</Link>

			<PageHeader
				title={onboarding?.name || 'Onboarding'}
				subtitle={`GET /backoffice/onboardings/pf/${id}`}
				actions={
					<>
						<Button variant="secondary" onClick={() => setShowRaw((prev) => !prev)}>
							{showRaw ? 'Ocultar JSON' : 'Ver JSON'}
						</Button>
						<Button variant="secondary" onClick={load}>
							<RefreshCw className="h-4 w-4" />
							Recarregar
						</Button>
						<Button variant="secondary" onClick={handleFinalize} disabled={isFinalizing}>
							{isFinalizing ? (
								<>
									<Loader size="sm" />
									Finalizando...
								</>
							) : (
								'Finalizar'
							)}
						</Button>
					</>
				}
			/>

			<Alert onDismiss={() => setError('')}>{error}</Alert>
			<Alert type="success" onDismiss={() => setFeedback('')}>
				{feedback}
			</Alert>

			{!onboarding ? (
				<EmptyState message="Onboarding nao encontrado" />
			) : (
				<div className="space-y-6">
					<Card title="Dados do cadastro">
						<DetailGrid>
							<DetailItem label="ID">
								<CopyableId value={onboarding.id} />
							</DetailItem>
							<DetailItem label="Status">
								<Badge tone={statusTone(onboarding.status)}>{onboarding.status}</Badge>
							</DetailItem>
							<DetailItem label="Verificacao BaaS">
								{onboarding.baasCustomerVerificationStatus && (
									<Badge tone={statusTone(onboarding.baasCustomerVerificationStatus)}>
										{onboarding.baasCustomerVerificationStatus}
									</Badge>
								)}
							</DetailItem>
							<DetailItem label="Tipo de cliente">{onboarding.clientType}</DetailItem>
							<DetailItem label="Nome">{onboarding.name}</DetailItem>
							<DetailItem label="Documento">{formatDocument(onboarding.document)}</DetailItem>
							<DetailItem label="Data de nascimento">
								{onboarding.birthDate
									? new Date(onboarding.birthDate).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
									: null}
							</DetailItem>
							<DetailItem label="Nome da mae">{onboarding.motherName}</DetailItem>
							<DetailItem label="E-mail">{onboarding.email}</DetailItem>
							<DetailItem label="Telefone">{phone}</DetailItem>
							<DetailItem label="Ocupacao">{onboarding.occupation}</DetailItem>
							<DetailItem label="Faixa de renda">{onboarding.incomeRange}</DetailItem>
							<DetailItem label="Renda mensal">{formatCurrency(onboarding.monthlyIncome)}</DetailItem>
							<DetailItem label="Nivel PEP">{onboarding.pepLevel}</DetailItem>
							<DetailItem label="Criado em">{formatDateTime(onboarding.createdAt)}</DetailItem>
						</DetailGrid>
					</Card>

					<Card title="Endereco">
						{!address ? (
							<EmptyState message="Sem endereco informado" />
						) : (
							<DetailGrid>
								<DetailItem label="CEP">{address.cep}</DetailItem>
								<DetailItem label="Logradouro">{address.logradouro}</DetailItem>
								<DetailItem label="Numero">{address.numero}</DetailItem>
								<DetailItem label="Complemento">{address.complemento}</DetailItem>
								<DetailItem label="Bairro">{address.bairro}</DetailItem>
								<DetailItem label="Cidade">{address.cidade}</DetailItem>
								<DetailItem label="UF">{address.uf}</DetailItem>
								<DetailItem label="Pais">{address.pais}</DetailItem>
							</DetailGrid>
						)}
					</Card>

					<Card title="Biometria e documentos">
						{onboarding.biometricLink && (
							<a
								href={onboarding.biometricLink}
								target="_blank"
								rel="noreferrer"
								className="mb-4 inline-flex items-center gap-1 text-primary-600 hover:underline"
							>
								Abrir link de biometria
								<ExternalLink className="h-3.5 w-3.5" />
							</a>
						)}

						{documents.length === 0 ? (
							<EmptyState message="Nenhum documento enviado" />
						) : (
							<div className="overflow-x-auto">
								<table className="min-w-full divide-y divide-gray-200 text-sm">
									<thead className="bg-gray-50">
										<tr>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Tipo
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Lado
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Analise imagem
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Face match
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Liveness
											</th>
											<th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
												Resultado
											</th>
										</tr>
									</thead>
									<tbody className="divide-y divide-gray-200">
										{documents.map((doc) => (
											<tr key={doc.uuid || `${doc.documentType}-${doc.documentSide}`}>
												<td className="px-4 py-2 text-gray-900">{doc.documentType}</td>
												<td className="px-4 py-2 text-gray-500">{doc.documentSide}</td>
												<td className="px-4 py-2 text-gray-500">
													{doc.baasImageAnalysisStatus || '—'}
												</td>
												<td className="px-4 py-2 text-gray-500">{doc.faceMatchStatus || '—'}</td>
												<td className="px-4 py-2 text-gray-500">
													{doc.baasLivenessStatus || '—'}
												</td>
												<td className="px-4 py-2">
													{doc.baasImageAnalysisResult ? (
														<Badge tone={statusTone(doc.baasImageAnalysisResult)}>
															{doc.baasImageAnalysisResult}
														</Badge>
													) : (
														'—'
													)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</Card>

					{showRaw && (
						<Card title="Resposta bruta">
							<JsonViewer data={onboarding} />
						</Card>
					)}
				</div>
			)}
		</div>
	)
}

export default OnboardingPfDetails
